import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { Autocomplete, Button } from "@truevault/aquilo";
import clsx from "clsx";
import type { AutocompleteItem } from "@truevault/aquilo/src/components/textInput/Autocomplete";
import React, { useEffect, useState } from "react";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import {
  useCreateManagedAdNetwork,
  useDeleteManagedAdNetwork,
  useManagedAdNetworksByManager,
} from "../../../hooks/useManagedAdNetworks";
import type { VendorInventoryItem } from "./vendorInventoryUtils";

/**
 * Designates which of an organization's data recipients TrueVault Connected System handles on their behalf
 * (POL-7958). Nested recipients are hidden from the Delete, Opt-Out and Access workflows and listed under TVCS
 * instead - see `vendorNesting.ts`.
 *
 * Rows are stored in `organization_managed_ad_network` with TVCS as the manager, the same table the ad-network
 * "managed by another vendor" UI writes. Unlike that UI this never passes `syncSurvey`: TVCS must not end up in the
 * selling & sharing survey's custom-audience answer, which drives DPA and selling prompts.
 */
export function ConnectedSystemVendorsSection({
  org,
  vendor,
  allVendors,
  portalAutocompleteDropdown = false,
  onVendorAdded,
  className,
}: {
  org: OrganizationDto;
  vendor: VendorInventoryItem;
  allVendors: VendorInventoryItem[];
  portalAutocompleteDropdown?: boolean;
  onVendorAdded?: () => void;
  className?: string;
}) {
  const connectedSystemRecipientId = vendor.recipientData?.id;

  const { data: nestedRelations = [], isLoading } = useManagedAdNetworksByManager(
    org.id,
    parseInt(connectedSystemRecipientId ?? ""),
  );
  const { mutateAsync: createManagedAdNetwork } = useCreateManagedAdNetwork(org.id);
  const { mutateAsync: deleteManagedAdNetwork } = useDeleteManagedAdNetwork(org.id);

  // Key to force-reset the Autocomplete input after each selection
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  // Wait for the newly added system to render before notifying the parent.
  const [pendingNetworkId, setPendingNetworkId] = useState<string | null>(null);

  useEffect(() => {
    if (pendingNetworkId && nestedRelations.some((r) => r.networkId === pendingNetworkId)) {
      onVendorAdded?.();
      setPendingNetworkId(null);
    }
  }, [nestedRelations, onVendorAdded, pendingNetworkId]);

  if (!connectedSystemRecipientId) {
    return null;
  }

  const nestedIds = new Set(nestedRelations.map((relation) => relation.networkId));
  const autocompleteItems: AutocompleteItem[] = allVendors
    .filter(
      (v) =>
        v.isRecipient &&
        v.recipientData &&
        v.vendorId !== vendor.vendorId &&
        !nestedIds.has(v.recipientData.id),
    )
    .map((v) => ({
      id: v.recipientData!.id,
      name: v.name,
      logoUrl: v.logoUrl,
    }));

  return (
    <div className={clsx(className)}>
      <div className="text-gray-700 font-medium">Systems {vendor.name} handles requests for</div>
      <div className="text-neutral-500">
        Add the systems this connector automates. They stop appearing on their own in the deletion,
        opt-out and access workflows, and are listed under this vendor instead.
      </div>

      <div className="mt-[10px]">
        <Autocomplete
          key={autocompleteKey}
          items={autocompleteItems}
          selectedItem={null}
          // show all possible options initially
          initialSuggestionsCount={autocompleteItems.length}
          onSelect={async (item) => {
            await createManagedAdNetwork({
              managerId: connectedSystemRecipientId,
              networkId: item.id,
            });
            setPendingNetworkId(item.id);
            setAutocompleteKey((k) => k + 1);
          }}
          leadingElement={
            <div className="w-[50px] h-full border border-solid border-neutral-400 rounded-l-sm flex items-center justify-center">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-neutral-500" />
            </div>
          }
          leadingInputPaddingPx={66}
          dropdownWidth="w-[417px]"
          portalDropdown={portalAutocompleteDropdown}
        />
      </div>

      {!isLoading && nestedRelations.length === 0 && (
        <div className="mt-[8px] text-neutral-500">
          No systems are handled by {vendor.name} yet.
        </div>
      )}

      {nestedRelations.length > 0 && (
        <div className="mt-[8px] space-y-[6px]">
          {nestedRelations.map((relation) => {
            const nestedVendor = allVendors.find(
              (v) => v.isRecipient && v.recipientData?.id === relation.networkId,
            );
            return (
              <div
                key={relation.id}
                className="flex items-center justify-between border border-solid border-neutral-200 rounded-[4px] px-[12px] py-[8px]"
              >
                <div className="flex items-center gap-[10px]">
                  {nestedVendor?.logoUrl && (
                    <img
                      src={nestedVendor.logoUrl}
                      alt={relation.networkName ?? nestedVendor.name}
                      className="h-6 w-6"
                    />
                  )}
                  <span className="text-[14px] font-medium text-gray-900">
                    {relation.networkName ?? nestedVendor?.name ?? "Unknown Vendor"}
                  </span>
                </div>
                <Button
                  color="tertiary"
                  size="s"
                  onClick={async () => {
                    await deleteManagedAdNetwork(relation.id);
                  }}
                >
                  Remove
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
